import { FormEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
	GENRE_OPTIONS,
	type Book,
	type BootstrapData,
	type Genre,
	type ListItem,
	type ListType,
	type Member,
	type Nomination,
	type ReadHistoryEntry,
	type ReadingHistoryStatus,
	type SearchBook,
} from '../types';
import { getBootstrap, getSession, login, logout, request, searchBooks } from './api';

type View = 'meeting' | 'nominations' | 'history' | `member/${string}`;
type ViewMode = 'full' | 'compact';
type PickerTarget = { kind: 'shelf'; memberId: string; listType: ListType } | { kind: 'nomination' } | { kind: 'history' };
type Mutate = (work: () => Promise<unknown>, message?: string) => Promise<boolean>;

const LIST_LABELS: Record<ListType, string> = {
	want_to_read: 'Want to read',
	recommend: 'Recommend',
};

function readViewMode(): ViewMode {
	return localStorage.getItem('book-club-view-mode') === 'compact' ? 'compact' : 'full';
}

function hasViewModePreference(): boolean {
	return localStorage.getItem('book-club-view-mode') === 'compact' || localStorage.getItem('book-club-view-mode') === 'full';
}

function isLikelyMobileDevice(): boolean {
	const navigatorWithHints = navigator as Navigator & { userAgentData?: { mobile?: boolean } };
	return (
		Boolean(navigatorWithHints.userAgentData?.mobile) ||
		window.matchMedia('(max-width: 760px)').matches ||
		(window.matchMedia('(pointer: coarse)').matches && window.matchMedia('(max-width: 1100px)').matches)
	);
}

function readView(): View {
	const value = window.location.hash.replace(/^#/u, '');
	if (value === 'nominations' || value === 'history' || value.startsWith('member/')) return value as View;
	return 'meeting';
}

function timeAgo(value: string): string {
	const timestamp = Date.parse(value.replace(' ', 'T') + (value.includes('Z') ? '' : 'Z'));
	const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
	if (seconds < 60) return 'just now';
	if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
	if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;
	return `${Math.floor(seconds / 86_400)}d ago`;
}

function formatMeetingDate(value: string | null): { day: string; detail: string } | null {
	if (!value) return null;
	const date = new Date(value);
	if (Number.isNaN(date.valueOf())) return null;
	return {
		day: new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }).format(date),
		detail: new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }).format(date),
	};
}

function parseHistoryDate(value: string | null): Date | null {
	if (!value) return null;
	const date = new Date(/^\d{4}-\d{2}-\d{2}$/u.test(value) ? `${value}T12:00:00` : value);
	return Number.isNaN(date.valueOf()) ? null : date;
}

function localDateTimeValue(value: string | null): string {
	const date = parseHistoryDate(value);
	if (!date) return '';
	return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

function initials(name: string): string {
	return name
		.split(/\s+/u)
		.map((part) => part[0])
		.join('')
		.slice(0, 2)
		.toUpperCase();
}

function Icon({ name, size = 18 }: { name: string; size?: number }) {
	const paths: Record<string, ReactNode> = {
		book: (
			<>
				<path d="M4 5.5A3.5 3.5 0 0 1 7.5 2H12v18H7.5A3.5 3.5 0 0 0 4 23.5V5.5Z" />
				<path d="M20 5.5A3.5 3.5 0 0 0 16.5 2H12v18h4.5a3.5 3.5 0 0 1 3.5 3.5V5.5Z" />
			</>
		),
		archive: (
			<>
				<path d="M4 7h16v14H4Z" />
				<path d="M3 3h18v4H3ZM9 12h6" />
			</>
		),
		calendar: (
			<>
				<rect x="3" y="5" width="18" height="16" rx="2" />
				<path d="M16 3v4M8 3v4M3 10h18" />
			</>
		),
		check: <path d="m5 12 4 4L19 6" />,
		chevron: <path d="m9 18 6-6-6-6" />,
		close: <path d="M6 6l12 12M18 6 6 18" />,
		edit: (
			<>
				<path d="M12 20h9" />
				<path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z" />
			</>
		),
		heart: (
			<path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.5 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z" />
		),
		lock: (
			<>
				<rect x="4" y="10" width="16" height="11" rx="2" />
				<path d="M8 10V7a4 4 0 0 1 8 0v3" />
			</>
		),
		message: (
			<>
				<path d="M21 15a4 4 0 0 1-4 4H8l-5 3 1.6-4.8A7.7 7.7 0 0 1 3 12c0-4 3.8-7 9-7s9 3 9 7Z" />
				<path d="M8 12h.01M12 12h.01M16 12h.01" />
			</>
		),
		monitor: (
			<>
				<rect x="3" y="4" width="18" height="13" rx="2" />
				<path d="M8 21h8M12 17v4" />
			</>
		),
		more: (
			<>
				<circle cx="5" cy="12" r="1" />
				<circle cx="12" cy="12" r="1" />
				<circle cx="19" cy="12" r="1" />
			</>
		),
		phone: (
			<>
				<rect x="6" y="2" width="12" height="20" rx="2" />
				<path d="M10 5h4M11 19h2" />
			</>
		),
		plus: <path d="M12 5v14M5 12h14" />,
		refresh: (
			<>
				<path d="M20 6v5h-5" />
				<path d="M4 18v-5h5" />
				<path d="M18.5 9A7 7 0 0 0 6 6.5L4 11M5.5 15A7 7 0 0 0 18 17.5l2-4.5" />
			</>
		),
		search: (
			<>
				<circle cx="11" cy="11" r="7" />
				<path d="m20 20-4-4" />
			</>
		),
		sparkle: (
			<>
				<path d="m12 3 1.2 4.2L17 9l-3.8 1.8L12 15l-1.2-4.2L7 9l3.8-1.8Z" />
				<path d="m19 15 .7 2.3L22 18l-2.3.7L19 21l-.7-2.3L16 18l2.3-.7Z" />
			</>
		),
		star: <path d="m12 3 2.7 5.5 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1-4.4-4.3 6.1-.9Z" />,
		trash: (
			<>
				<path d="M4 7h16M9 7V4h6v3M7 7l1 14h8l1-14" />
				<path d="M10 11v6M14 11v6" />
			</>
		),
		user: (
			<>
				<circle cx="12" cy="8" r="4" />
				<path d="M4 22a8 8 0 0 1 16 0" />
			</>
		),
	};
	return (
		<svg
			aria-hidden="true"
			className="icon"
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			{paths[name]}
		</svg>
	);
}

function Avatar({ member, small = false }: { member: Member; small?: boolean }) {
	return (
		<span
			className={`avatar ${small ? 'avatar--small' : ''}`}
			style={{ '--avatar-colour': member.accent } as React.CSSProperties}
			aria-hidden="true"
		>
			{initials(member.name)}
		</span>
	);
}

function BookCover({ book, compact = false }: { book: Pick<Book, 'title' | 'coverUrl'>; compact?: boolean }) {
	const [failed, setFailed] = useState(false);
	return (
		<div className={`book-cover ${compact ? 'book-cover--compact' : ''}`}>
			{book.coverUrl && !failed ? (
				<img src={book.coverUrl} alt={`Cover of ${book.title}`} onError={() => setFailed(true)} />
			) : (
				<div className="book-cover__fallback">
					<span>{book.title.slice(0, 1)}</span>
					<small>{book.title}</small>
				</div>
			)}
		</div>
	);
}

function ProgressRing({ complete, total }: { complete: number; total: number }) {
	const fraction = total ? complete / total : 0;
	return (
		<div className="progress-ring" style={{ '--progress': `${fraction * 360}deg` } as React.CSSProperties}>
			<span>{complete}</span>
			<small>of {total}</small>
		</div>
	);
}

function Modal({ title, children, onClose, wide = false }: { title: string; children: ReactNode; onClose: () => void; wide?: boolean }) {
	const closeRef = useRef<HTMLButtonElement>(null);
	useEffect(() => {
		closeRef.current?.focus();
		const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [onClose]);
	return (
		<div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
			<section className={`modal ${wide ? 'modal--wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby="modal-title">
				<header className="modal__header">
					<div>
						<span className="eyebrow">Book club</span>
						<h2 id="modal-title">{title}</h2>
					</div>
					<button ref={closeRef} className="icon-button" onClick={onClose} aria-label="Close">
						<Icon name="close" />
					</button>
				</header>
				{children}
			</section>
		</div>
	);
}

function LoginScreen({ onSuccess }: { onSuccess: () => Promise<void> }) {
	const [password, setPassword] = useState('');
	const [error, setError] = useState('');
	const [busy, setBusy] = useState(false);
	const submit = async (event: FormEvent) => {
		event.preventDefault();
		setBusy(true);
		setError('');
		try {
			await login(password);
			await onSuccess();
		} catch (cause) {
			setError(cause instanceof Error ? cause.message : 'Please try again.');
		} finally {
			setBusy(false);
		}
	};
	return (
		<main className="login-screen">
			<div className="login-glow login-glow--one" />
			<div className="login-glow login-glow--two" />
			<section className="login-card">
				<div className="login-mark">
					<Icon name="book" size={30} />
				</div>
				<span className="eyebrow">A private corner</span>
				<h1>Book Club</h1>
				<p>Come in, the kettle’s on and the shelves are getting restless.</p>
				<form onSubmit={submit}>
					<label htmlFor="password">Club password</label>
					<div className="password-field">
						<Icon name="lock" />
						<input
							id="password"
							type="password"
							autoComplete="current-password"
							value={password}
							onChange={(event) => setPassword(event.target.value)}
							autoFocus
						/>
					</div>
					{error && (
						<p className="form-error" role="alert">
							{error}
						</p>
					)}
					<button className="button button--primary button--wide" disabled={busy || !password}>
						{busy ? 'Opening…' : 'Open the door'}
						<Icon name="chevron" />
					</button>
				</form>
				<small className="login-footnote">One password, six readers, zero fuss.</small>
			</section>
		</main>
	);
}

function IdentityChooser({ members, onChoose, onClose }: { members: Member[]; onChoose: (id: string) => void; onClose?: () => void }) {
	const body = (
		<div className="identity-grid">
			{members.map((member) => (
				<button key={member.id} className="identity-card" onClick={() => onChoose(member.id)}>
					<Avatar member={member} />
					<span>{member.name}</span>
					<small>This is me</small>
				</button>
			))}
		</div>
	);
	if (onClose)
		return (
			<Modal title="Who are you browsing as?" onClose={onClose}>
				{body}
			</Modal>
		);
	return (
		<div className="identity-screen">
			<div className="identity-panel">
				<span className="eyebrow">Welcome in</span>
				<h1>Who’s reading?</h1>
				<p>This only remembers your choice on this device. Anyone can still edit anything.</p>
				{body}
			</div>
		</div>
	);
}

function CompactModePrompt({ onChoose }: { onChoose: (mode: ViewMode) => void }) {
	return (
		<Modal title="Would you like a lighter mobile view?" onClose={() => onChoose('full')}>
			<div className="view-mode-prompt">
				<p>
					This looks like a smaller or touch-first screen. Compact view keeps the same shared club data, but brings the everyday actions
					forward and folds quieter extras away.
				</p>
				<div className="view-mode-choices">
					<button className="view-mode-choice view-mode-choice--compact" onClick={() => onChoose('compact')}>
						<span className="view-mode-choice__icon">
							<Icon name="phone" size={24} />
						</span>
						<span>
							<strong>Use compact view</strong>
							<small>Bottom navigation, core actions first, extras tucked away</small>
						</span>
						<Icon name="chevron" />
					</button>
					<button className="view-mode-choice" onClick={() => onChoose('full')}>
						<span className="view-mode-choice__icon">
							<Icon name="monitor" size={24} />
						</span>
						<span>
							<strong>Keep the full site</strong>
							<small>The responsive version with everything visible</small>
						</span>
						<Icon name="chevron" />
					</button>
				</div>
				<small className="view-mode-prompt__note">You can switch at any time from More or the footer.</small>
			</div>
		</Modal>
	);
}

function MeetingEditor({
	data,
	actorId,
	onSave,
	onClose,
}: {
	data: BootstrapData;
	actorId: string;
	onSave: (body: Record<string, unknown>) => Promise<boolean>;
	onClose: () => void;
}) {
	const meeting = data.meeting;
	const initialLocal = meeting.startsAt ? meeting.startsAt.slice(0, 16) : '';
	const [venue, setVenue] = useState(meeting.venue ?? '');
	const [startsAt, setStartsAt] = useState(initialLocal);
	const [notes, setNotes] = useState(meeting.notes);
	const [busy, setBusy] = useState(false);
	return (
		<Modal title="Plan the next meeting" onClose={onClose}>
			<form
				className="stack-form"
				onSubmit={async (event) => {
					event.preventDefault();
					setBusy(true);
					try {
						if (await onSave({ actorId, venue, startsAt: startsAt ? new Date(startsAt).toISOString() : null, notes })) onClose();
					} finally {
						setBusy(false);
					}
				}}
			>
				<label>
					Venue
					<input value={venue} onChange={(event) => setVenue(event.target.value)} placeholder="e.g. The library room" />
				</label>
				<label>
					Date and time
					<input type="datetime-local" value={startsAt} onChange={(event) => setStartsAt(event.target.value)} />
				</label>
				<label>
					Useful note
					<textarea
						rows={3}
						maxLength={600}
						value={notes}
						onChange={(event) => setNotes(event.target.value)}
						placeholder="Anything people should bring or know?"
					/>
				</label>
				<div className="modal__actions">
					<button type="button" className="button button--ghost" onClick={onClose}>
						Cancel
					</button>
					<button className="button button--primary" disabled={busy}>
						{busy ? 'Saving…' : 'Save meeting'}
					</button>
				</div>
			</form>
		</Modal>
	);
}

function DatePollPanel({ data, actorId, mutate }: { data: BootstrapData; actorId: string; mutate: Mutate }) {
	const [adding, setAdding] = useState(false);
	const [startsAt, setStartsAt] = useState('');
	const [busy, setBusy] = useState(false);
	const memberById = new Map(data.members.map((member) => [member.id, member]));
	return (
		<section className="panel date-poll page-section">
			<div className="section-heading">
				<div>
					<span className="eyebrow">Find a date</span>
					<h2>Meeting date poll</h2>
					<p>Tick every date you can make — and whether your place is available. Untick anything whenever plans change.</p>
				</div>
				<button className="button button--small" onClick={() => setAdding((value) => !value)}>
					<Icon name={adding ? 'close' : 'plus'} /> {adding ? 'Cancel' : 'Suggest a date'}
				</button>
			</div>
			{adding && (
				<form
					className="date-poll__add"
					onSubmit={async (event) => {
						event.preventDefault();
						if (!startsAt) return;
						setBusy(true);
						const saved = await mutate(
							() => request('/api/date-options', { actorId, startsAt: new Date(startsAt).toISOString() }),
							'Date added to the poll',
						);
						setBusy(false);
						if (saved) {
							setStartsAt('');
							setAdding(false);
						}
					}}
				>
					<label>
						Suggested date and time
						<input type="datetime-local" value={startsAt} onChange={(event) => setStartsAt(event.target.value)} required />
					</label>
					<button className="button button--primary" disabled={busy || !startsAt}>
						{busy ? 'Adding…' : 'Add to poll'}
					</button>
				</form>
			)}
			{data.meeting.dateOptions.length ? (
				<div className="date-poll__grid">
					{data.meeting.dateOptions.map((option) => {
						const date = new Date(option.startsAt);
						const response = option.responses.find((item) => item.memberId === actorId);
						const attending = option.responses.filter((item) => item.canAttend);
						const hosting = option.responses.filter((item) => item.canHost);
						const chosen = data.meeting.startsAt && new Date(data.meeting.startsAt).getTime() === date.getTime();
						return (
							<article className={`date-card ${chosen ? 'is-chosen' : ''}`} key={option.id}>
								<div className="date-card__date">
									<span>{new Intl.DateTimeFormat('en-GB', { month: 'short' }).format(date)}</span>
									<strong>{date.getDate()}</strong>
								</div>
								<div className="date-card__body">
									<div className="date-card__title">
										<div>
											<h3>{new Intl.DateTimeFormat('en-GB', { weekday: 'long' }).format(date)}</h3>
											<p>{new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }).format(date)}</p>
										</div>
										{chosen && (
											<span className="chosen-label">
												<Icon name="check" size={13} /> chosen
											</span>
										)}
									</div>
									<div className="date-card__people">
										<span>{attending.length} can make it</span>
										<div className="mini-avatars">
											{attending.map((item) => {
												const member = memberById.get(item.memberId);
												return member ? <Avatar key={member.id} member={member} small /> : null;
											})}
										</div>
									</div>
									{hosting.length > 0 && (
										<p className="host-line">
											Can host:{' '}
											{hosting
												.map((item) => memberById.get(item.memberId)?.name)
												.filter(Boolean)
												.join(', ')}
										</p>
									)}
									<div className="date-card__checks">
										<button
											className={response?.canAttend ? 'is-checked' : ''}
											onClick={() =>
												void mutate(() =>
													request('/api/date-poll', { actorId, optionId: option.id, field: 'canAttend', value: !response?.canAttend }),
												)
											}
										>
											<span className="check-box">{response?.canAttend && <Icon name="check" size={14} />}</span> I can make it
										</button>
										<button
											className={response?.canHost ? 'is-checked' : ''}
											onClick={() =>
												void mutate(() =>
													request('/api/date-poll', { actorId, optionId: option.id, field: 'canHost', value: !response?.canHost }),
												)
											}
										>
											<span className="check-box">{response?.canHost && <Icon name="check" size={14} />}</span> I can host
										</button>
									</div>
									<div className="date-card__actions">
										{!chosen ? (
											<button
												className="text-button"
												onClick={() =>
													void mutate(() => request(`/api/date-options/${option.id}/choose`, { actorId }), 'Meeting date chosen')
												}
											>
												Choose this date
											</button>
										) : (
											<button
												className="text-button"
												onClick={() =>
													void mutate(() => request('/api/meeting/date', { actorId, startsAt: null }), 'Date put back to undecided')
												}
											>
												Reopen choice
											</button>
										)}
										<button
											className="danger-link"
											onClick={() => {
												if (window.confirm('Remove this date from the poll?'))
													void mutate(() => request(`/api/date-options/${option.id}`, { actorId }, 'DELETE'), 'Date removed');
											}}
										>
											Remove
										</button>
									</div>
								</div>
							</article>
						);
					})}
				</div>
			) : (
				<div className="date-poll__empty">
					<Icon name="calendar" size={27} />
					<p>No dates suggested yet. Add a couple and see where the ticks gather.</p>
				</div>
			)}
		</section>
	);
}

function QuestionJarPanel({ data, actorId, mutate }: { data: BootstrapData; actorId: string; mutate: Mutate }) {
	const [question, setQuestion] = useState('');
	const [spoilerScope, setSpoilerScope] = useState<'spoiler_free' | 'whole_book'>('whole_book');
	const [editingId, setEditingId] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const jar = data.meeting.questionJar;
	const memberById = new Map(data.members.map((member) => [member.id, member]));
	return (
		<section className={`panel question-jar-panel ${jar.revealed ? 'is-open' : ''}`}>
			<div className="question-jar__intro">
				<div className="question-jar__illustration" aria-hidden="true">
					<div className="jar-lid" />
					<div className="jar-glass">
						{Array.from({ length: Math.min(jar.questions.length, 6) }, (_, index) => (
							<i key={index} />
						))}
					</div>
				</div>
				<div>
					<span className="eyebrow">For the table</span>
					<h2>{jar.revealed ? 'The question jar is open' : 'The sealed question jar'}</h2>
					<p>
						{jar.revealed
							? 'Take turns drawing from the questions below.'
							: `${jar.questions.length} ${jar.questions.length === 1 ? 'question is' : 'questions are'} tucked away. Only your own stays readable until the meeting.`}
					</p>
				</div>
			</div>
			<form
				className="question-form"
				onSubmit={async (event) => {
					event.preventDefault();
					if (!question.trim()) return;
					setBusy(true);
					const saved = await mutate(
						() =>
							request(
								editingId ? `/api/questions/${editingId}` : '/api/questions',
								{ actorId, question, spoilerScope },
								editingId ? 'PATCH' : 'POST',
							),
						editingId ? 'Question updated' : 'Question sealed in the jar',
					);
					if (saved) {
						setQuestion('');
						setEditingId(null);
					}
					setBusy(false);
				}}
			>
				<label>
					Your discussion question
					<textarea
						rows={2}
						maxLength={500}
						value={question}
						onChange={(event) => setQuestion(event.target.value)}
						placeholder="Something you want everyone to answer…"
					/>
				</label>
				<div className="question-form__foot">
					<label className="select-label">
						<span>Spoilers</span>
						<select value={spoilerScope} onChange={(event) => setSpoilerScope(event.target.value as typeof spoilerScope)}>
							<option value="whole_book">Whole book</option>
							<option value="spoiler_free">Spoiler-free</option>
						</select>
					</label>
					<button className="button button--primary" disabled={busy || !question.trim()}>
						<Icon name={editingId ? 'edit' : 'lock'} size={15} /> {busy ? 'Saving…' : editingId ? 'Save changes' : 'Seal question'}
					</button>
					{editingId && (
						<button
							type="button"
							className="text-button"
							onClick={() => {
								setEditingId(null);
								setQuestion('');
							}}
						>
							Cancel edit
						</button>
					)}
				</div>
			</form>
			{jar.questions.length > 0 && (
				<div className="sealed-stack">
					{jar.questions.map((item, index) => {
						const member = memberById.get(item.memberId);
						return (
							<article
								key={item.id}
								className={item.question ? 'is-readable' : ''}
								style={{ '--note-tilt': `${(index % 3) - 1}deg` } as React.CSSProperties}
							>
								<span className="question-number">#{index + 1}</span>
								<p>{item.question ?? 'A sealed question for the meeting'}</p>
								<div className="question-card__footer">
									<span>{item.spoilerScope === 'spoiler_free' ? 'Spoiler-free' : 'Whole book'}</span>
									{jar.revealed && member && <span>from {member.name}</span>}
									{item.isOwn && item.question && (
										<span className="question-card__actions">
											<button
												onClick={() => {
													setEditingId(item.id);
													setQuestion(item.question ?? '');
													setSpoilerScope(item.spoilerScope);
												}}
											>
												Edit
											</button>
											<button
												className="danger-link"
												onClick={() => void mutate(() => request(`/api/questions/${item.id}`, { actorId }, 'DELETE'), 'Question removed')}
											>
												Remove
											</button>
										</span>
									)}
								</div>
							</article>
						);
					})}
				</div>
			)}
			{!jar.revealed && jar.questions.length > 0 && (
				<button
					className="button button--light open-jar"
					onClick={() => {
						if (window.confirm('Open the jar for everyone? This cannot be re-sealed.'))
							void mutate(() => request('/api/questions/reveal', { actorId }), 'The question jar is open');
					}}
				>
					<Icon name="sparkle" /> Open at the meeting
				</button>
			)}
		</section>
	);
}

function downloadCalendar(data: BootstrapData) {
	if (!data.meeting.startsAt) return;
	const start = new Date(data.meeting.startsAt);
	const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
	const stamp = (date: Date) =>
		date
			.toISOString()
			.replace(/[-:]/gu, '')
			.replace(/\.\d{3}/u, '');
	const escape = (value: string) => value.replaceAll('\\', '\\\\').replaceAll(',', '\\,').replaceAll(';', '\\;').replaceAll('\n', '\\n');
	const ics = [
		'BEGIN:VCALENDAR',
		'VERSION:2.0',
		'PRODID:-//book-club.uk//Book Club//EN',
		'BEGIN:VEVENT',
		`UID:${data.meeting.id}@book-club.uk`,
		`DTSTAMP:${stamp(new Date())}`,
		`DTSTART:${stamp(start)}`,
		`DTEND:${stamp(end)}`,
		`SUMMARY:${escape(`Book Club: ${data.meeting.book.title}`)}`,
		`LOCATION:${escape(data.meeting.venue ?? '')}`,
		`DESCRIPTION:${escape(data.meeting.notes || `Discussing ${data.meeting.book.title} by ${data.meeting.book.author}`)}`,
		'END:VEVENT',
		'END:VCALENDAR',
	].join('\r\n');
	const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
	const link = document.createElement('a');
	link.href = url;
	link.download = 'book-club.ics';
	link.click();
	URL.revokeObjectURL(url);
}

function CompleteMeetingModal({
	data,
	actorId,
	mutate,
	onClose,
}: {
	data: BootstrapData;
	actorId: string;
	mutate: Mutate;
	onClose: () => void;
}) {
	const [busy, setBusy] = useState(false);
	const selected = data.cycle.podium.find((entry) => entry.nominationId === data.cycle.selectedNominationId);
	if (!selected) return null;
	return (
		<Modal title="Complete this meeting and move on" onClose={onClose}>
			<div className="rollover-preview">
				<div className="rollover-preview__books">
					<div>
						<span className="eyebrow">Archive</span>
						<BookCover book={data.meeting.book} compact />
						<strong>{data.meeting.book.title}</strong>
					</div>
					<span className="rollover-arrow" aria-hidden="true">
						<Icon name="chevron" size={24} />
					</span>
					<div>
						<span className="eyebrow">Read next</span>
						<BookCover book={selected.book} compact />
						<strong>{selected.book.title}</strong>
					</div>
				</div>
				<ul className="rollover-checklist">
					<li>
						<Icon name="archive" size={16} /> Preserve this meeting, its reading ticks and question jar in Read history
					</li>
					<li>
						<Icon name="book" size={16} /> Open {selected.book.title} as the current club read
					</li>
					<li>
						<Icon name="refresh" size={16} /> Start a clean nomination and voting round for the book after that
					</li>
				</ul>
				<p className="rollover-note">
					Nothing from this round is erased. You can undo the handover while the new meeting and ballot are still untouched.
				</p>
				<div className="modal__actions">
					<button type="button" className="button button--ghost" onClick={onClose} disabled={busy}>
						Not yet
					</button>
					<button
						type="button"
						className="button button--primary"
						disabled={busy}
						onClick={async () => {
							setBusy(true);
							const saved = await mutate(
								() => request('/api/lifecycle/complete', { actorId }),
								`${data.meeting.book.title} archived — now reading ${selected.book.title}`,
							);
							setBusy(false);
							if (saved) onClose();
						}}
					>
						<Icon name="archive" /> {busy ? 'Moving the bookmarks…' : 'Complete meeting & move on'}
					</button>
				</div>
			</div>
		</Modal>
	);
}

function UndoRolloverModal({
	data,
	actorId,
	mutate,
	onClose,
}: {
	data: BootstrapData;
	actorId: string;
	mutate: Mutate;
	onClose: () => void;
}) {
	const [busy, setBusy] = useState(false);
	const rollover = data.lastRollover;
	if (!rollover?.canUndo) return null;
	return (
		<Modal title="Undo the last handover?" onClose={onClose}>
			<div className="undo-rollover">
				<p>
					This will restore <strong>{rollover.previousBook.title}</strong> as the current meeting, put the completed ballot back, and remove
					the untouched new meeting and round.
				</p>
				<p>No archived choices will be lost because nothing new has been added yet.</p>
				<div className="modal__actions">
					<button type="button" className="button button--ghost" onClick={onClose} disabled={busy}>
						Keep things as they are
					</button>
					<button
						type="button"
						className="button button--danger"
						disabled={busy}
						onClick={async () => {
							setBusy(true);
							const saved = await mutate(() => request('/api/lifecycle/undo', { actorId }), `${rollover.previousBook.title} restored`);
							setBusy(false);
							if (saved) onClose();
						}}
					>
						<Icon name="refresh" /> {busy ? 'Restoring…' : 'Undo handover'}
					</button>
				</div>
			</div>
		</Modal>
	);
}

function MeetingPage({
	data,
	actorId,
	mutate,
	compactMode,
}: {
	data: BootstrapData;
	actorId: string;
	mutate: Mutate;
	compactMode: boolean;
}) {
	const [editing, setEditing] = useState(false);
	const [completing, setCompleting] = useState(false);
	const [undoing, setUndoing] = useState(false);
	const meetingDate = formatMeetingDate(data.meeting.startsAt);
	const finished = data.meeting.readingStatus.filter((status) => status.completed).length;
	const memberById = new Map(data.members.map((member) => [member.id, member]));
	const selectedNextRead = data.cycle.podium.find((entry) => entry.nominationId === data.cycle.selectedNominationId);
	const meetingExtras = (
		<div className="lower-grid page-section">
			<section className="panel prompt-panel">
				<span className="eyebrow">Conversation starters</span>
				<h2>Save these for the table</h2>
				<div className="prompt-list">
					<blockquote>Which character would you trust with a secret?</blockquote>
					<blockquote>What changed most between your first impression and the final page?</blockquote>
					<blockquote>Which scene would you most like to step into?</blockquote>
				</div>
			</section>
			<section className="panel activity-panel">
				<span className="eyebrow">From the shelves</span>
				<h2>Latest activity</h2>
				<ul>
					{data.activity.slice(0, 5).map((item) => (
						<li key={item.id}>
							<span className="activity-dot" />{' '}
							<div>
								{item.message}
								<small>{timeAgo(item.createdAt)}</small>
							</div>
						</li>
					))}
				</ul>
			</section>
		</div>
	);
	return (
		<>
			{data.lastRollover?.canUndo && (
				<section className="rollover-undo-banner page-section">
					<div>
						<Icon name="refresh" />
						<span>
							<strong>Just moved on?</strong>
							<small>The previous meeting can still be restored because this new round is untouched.</small>
						</span>
					</div>
					<button className="text-button" onClick={() => setUndoing(true)}>
						Undo handover
					</button>
				</section>
			)}
			<section className="meeting-hero page-section">
				<div className="meeting-hero__cover">
					<span className="cover-shadow" />
					<BookCover book={data.meeting.book} />
				</div>
				<div className="meeting-hero__copy">
					<span className="eyebrow">
						<span className="live-dot" /> Reading now
					</span>
					<h1>{data.meeting.book.title}</h1>
					<p className="book-author">by {data.meeting.book.author}</p>
					{data.meeting.book.firstPublishYear && <p className="book-meta">First published {data.meeting.book.firstPublishYear}</p>}
					<p className="meeting-intro">
						Our current club read — keep progress here and save any tempting discussion questions for the jar.
					</p>
					<div className="hero-actions">
						{data.meeting.book.sourceUrl && (
							<a className="button button--light" href={data.meeting.book.sourceUrl} target="_blank" rel="noreferrer">
								Book details <Icon name="chevron" />
							</a>
						)}
						<button className="button button--bare" onClick={() => setEditing(true)}>
							<Icon name="edit" /> Edit meeting
						</button>
					</div>
				</div>
				<div className="meeting-hero__progress">
					<ProgressRing complete={finished} total={data.members.length} />
					<span>finished reading</span>
				</div>
			</section>

			<div className="meeting-grid page-section">
				<section className="panel logistics-panel">
					<div className="section-heading">
						<div>
							<span className="eyebrow">Next gathering</span>
							<h2>When & where</h2>
						</div>
						<button className="icon-button" onClick={() => setEditing(true)} aria-label="Edit meeting">
							<Icon name="edit" />
						</button>
					</div>
					<div className="logistics-list">
						<div className="logistics-item">
							<span className="detail-icon">
								<Icon name="calendar" />
							</span>
							<div>
								<small>Date</small>
								<strong>{meetingDate?.day ?? 'Not decided yet'}</strong>
								{meetingDate && <span>{meetingDate.detail}</span>}
							</div>
						</div>
						<div className="logistics-item">
							<span className="detail-icon">
								<Icon name="book" />
							</span>
							<div>
								<small>Venue</small>
								<strong>{data.meeting.venue ?? 'Not decided yet'}</strong>
								{!data.meeting.venue && <span>Someone pick a cosy spot</span>}
							</div>
						</div>
					</div>
					{data.meeting.notes && <p className="meeting-note">{data.meeting.notes}</p>}
					{data.meeting.startsAt && (
						<button className="text-button" onClick={() => downloadCalendar(data)}>
							<Icon name="calendar" /> Add to calendar
						</button>
					)}
				</section>

				<section className="panel reading-panel">
					<div className="section-heading">
						<div>
							<span className="eyebrow">Reading roll-call</span>
							<h2>Who’s finished?</h2>
						</div>
						<span className="count-pill">
							{finished}/{data.members.length}
						</span>
					</div>
					<p>Tick your name when you’ve reached the final page. You can untick it if needed; no spoilers here.</p>
					<div className="member-checks">
						{data.meeting.readingStatus.map((status) => {
							const member = memberById.get(status.memberId)!;
							return (
								<button
									key={status.memberId}
									className={`member-check ${status.completed ? 'is-complete' : ''}`}
									onClick={() =>
										mutate(
											() => request('/api/reading-status', { actorId, memberId: status.memberId, completed: !status.completed }),
											status.completed ? `${member.name} marked as still reading` : `${member.name} finished the book`,
										)
									}
								>
									<Avatar member={member} small />
									<span>{member.name}</span>
									<span className="check-box">{status.completed && <Icon name="check" size={15} />}</span>
								</button>
							);
						})}
					</div>
				</section>
			</div>

			<DatePollPanel data={data} actorId={actorId} mutate={mutate} />

			{compactMode ? (
				<details className="compact-extras page-section">
					<summary>
						<span>
							<Icon name="sparkle" /> Conversation starters & recent activity
						</span>
						<small>Optional extras</small>
					</summary>
					{meetingExtras}
				</details>
			) : (
				meetingExtras
			)}
			<div className="page-section">
				<QuestionJarPanel data={data} actorId={actorId} mutate={mutate} />
			</div>
			<section className={`handover-panel page-section ${selectedNextRead ? 'is-ready' : ''}`}>
				<div className="handover-panel__mark" aria-hidden="true">
					<Icon name={selectedNextRead ? 'book' : 'archive'} size={25} />
				</div>
				<div className="handover-panel__copy">
					<span className="eyebrow">Close the chapter</span>
					{selectedNextRead ? (
						<>
							<h2>Ready to move on to {selectedNextRead.book.title}</h2>
							<p>After this gathering, archive {data.meeting.book.title} and open a fresh meeting and nomination round in one step.</p>
						</>
					) : data.cycle.allVotingComplete ? (
						<>
							<h2>Choose the next read from the podium</h2>
							<p>The result is ready. Pick any podium book before completing this meeting.</p>
						</>
					) : (
						<>
							<h2>The next chapter will appear here</h2>
							<p>Once everyone finishes voting and a podium book is chosen, this meeting can be completed and archived.</p>
						</>
					)}
				</div>
				{selectedNextRead ? (
					<button className="button button--primary" onClick={() => setCompleting(true)}>
						<Icon name="archive" /> Complete meeting
					</button>
				) : (
					<a className="button button--ghost" href="#nominations">
						Go to nominations
					</a>
				)}
			</section>
			{editing && (
				<MeetingEditor
					data={data}
					actorId={actorId}
					onClose={() => setEditing(false)}
					onSave={(body) => mutate(() => request('/api/meeting', body, 'PATCH'), 'Meeting details updated')}
				/>
			)}
			{completing && <CompleteMeetingModal data={data} actorId={actorId} mutate={mutate} onClose={() => setCompleting(false)} />}
			{undoing && <UndoRolloverModal data={data} actorId={actorId} mutate={mutate} onClose={() => setUndoing(false)} />}
		</>
	);
}

function PhaseTracker({
	data,
	phase,
	actorId,
	mutate,
}: {
	data: BootstrapData;
	phase: 'nominating' | 'voting';
	actorId: string;
	mutate: Mutate;
}) {
	const checks = data.cycle.checks.filter((check) => check.phase === phase);
	const activeCheck = checks.find((check) => check.memberId === actorId);
	const disabled = phase === 'voting' && !data.cycle.allNominationsComplete;
	return (
		<div className={`phase-card ${checks.every((check) => check.completed) ? 'is-complete' : ''}`}>
			<div className="phase-card__number">{phase === 'nominating' ? '1' : '2'}</div>
			<div className="phase-card__body">
				<span className="eyebrow">{phase === 'nominating' ? 'Build the ballot' : 'Make your picks'}</span>
				<h3>Finished {phase}?</h3>
				<div className="mini-avatars">
					{data.members.map((member) => {
						const checked = checks.some((check) => check.memberId === member.id && check.completed);
						return (
							<span key={member.id} className={checked ? 'is-done' : ''} title={`${member.name}: ${checked ? 'done' : 'not yet'}`}>
								<Avatar member={member} small />
								{checked && (
									<i>
										<Icon name="check" size={11} />
									</i>
								)}
							</span>
						);
					})}
				</div>
			</div>
			<button
				className={`done-button ${activeCheck?.completed ? 'is-done' : ''}`}
				disabled={disabled}
				onClick={() =>
					mutate(
						() => request('/api/checks', { actorId, phase, completed: !activeCheck?.completed }),
						activeCheck?.completed ? 'Marked as not finished' : `You’ve finished ${phase}`,
					)
				}
			>
				{activeCheck?.completed ? (
					<>
						<Icon name="check" /> Done · undo
					</>
				) : (
					'I’m finished'
				)}
			</button>
		</div>
	);
}

function NominationCard({
	nomination,
	data,
	actorId,
	canVote,
	mutate,
}: {
	nomination: Nomination;
	data: BootstrapData;
	actorId: string;
	canVote: boolean;
	mutate: Mutate;
}) {
	const nominator = data.members.find((member) => member.id === nomination.nominatedBy);
	return (
		<article className={`nomination-card ${nomination.votedByActiveMember ? 'has-vote' : ''}`}>
			<BookCover book={nomination.book} compact />
			<div className="nomination-card__copy">
				<h3>{nomination.book.title}</h3>
				<p>{nomination.book.author}</p>
				{nominator && (
					<small>
						Nominated by{' '}
						<span>
							<Avatar member={nominator} small /> {nominator.name}
						</span>
					</small>
				)}
			</div>
			<div className="nomination-card__actions">
				{canVote && (
					<button
						className={`vote-button ${nomination.votedByActiveMember ? 'is-voted' : ''}`}
						onClick={() =>
							mutate(
								() => request('/api/votes/toggle', { actorId, nominationId: nomination.id }),
								nomination.votedByActiveMember ? 'Vote removed' : 'Vote added',
							)
						}
						aria-pressed={nomination.votedByActiveMember}
					>
						<Icon name="heart" />
						{nomination.votedByActiveMember ? 'Upvoted' : 'Upvote'}
					</button>
				)}
				<button
					className="icon-button icon-button--quiet"
					onClick={() => {
						if (window.confirm(`Remove ${nomination.book.title} from the ballot?`))
							void mutate(() => request(`/api/nominations/${nomination.id}`, { actorId }, 'DELETE'), 'Nomination removed');
					}}
					aria-label={`Remove ${nomination.book.title}`}
				>
					<Icon name="trash" size={16} />
				</button>
			</div>
		</article>
	);
}

function Podium({ data, actorId, mutate }: { data: BootstrapData; actorId: string; mutate: Mutate }) {
	const ordered = [
		data.cycle.podium.find((entry) => entry.rank === 2),
		data.cycle.podium.find((entry) => entry.rank === 1),
		data.cycle.podium.find((entry) => entry.rank === 3),
	].filter(Boolean) as BootstrapData['cycle']['podium'];
	return (
		<section className="results-reveal">
			<div className="results-heading">
				<span className="eyebrow">
					<Icon name="sparkle" /> The votes are in
				</span>
				<h2>Our winning shelf</h2>
				<p>The top three, revealed only after everyone finished voting.</p>
			</div>
			<div className="podium">
				{ordered.map((entry) => {
					const selected = data.cycle.selectedNominationId === entry.nominationId;
					return (
						<article key={entry.nominationId} className={`podium-place podium-place--${entry.rank} ${selected ? 'is-selected' : ''}`}>
							<span className="podium-rank">{entry.rank}</span>
							<BookCover book={entry.book} compact />
							<h3>{entry.book.title}</h3>
							<p>{entry.book.author}</p>
							<strong>
								{entry.votes} {entry.votes === 1 ? 'vote' : 'votes'}
							</strong>
							{entry.tieBreak && <small>Position settled by a fair, stable draw</small>}
							<button
								className={`podium-choice ${selected ? 'is-selected' : ''}`}
								disabled={selected}
								onClick={() =>
									void mutate(
										() => request('/api/cycle/selection', { actorId, nominationId: entry.nominationId }),
										`${entry.book.title} is queued as the next read`,
									)
								}
							>
								<Icon name={selected ? 'check' : 'book'} size={15} /> {selected ? 'Chosen next' : 'Make our next read'}
							</button>
							<div className="podium-step">{entry.rank}</div>
						</article>
					);
				})}
			</div>
			{data.cycle.selectedNominationId && (
				<div className="next-read-selection" role="status">
					<div>
						<Icon name="check" />
						<span>
							<strong>Next read chosen</strong>
							<small>You can change it here until the current meeting is archived.</small>
						</span>
					</div>
					<button
						className="text-button"
						onClick={() => void mutate(() => request('/api/cycle/selection', { actorId, nominationId: null }), 'Next-read choice reopened')}
					>
						Clear choice
					</button>
				</div>
			)}
		</section>
	);
}

function GenreCloud({ data }: { data: BootstrapData }) {
	const counts = new Map<Genre, number>();
	Object.values(data.memberGenres).forEach((genres) => genres.forEach((genre) => counts.set(genre, (counts.get(genre) ?? 0) + 2)));
	data.cycle.nominations.forEach((nomination) =>
		nomination.book.genres.forEach((genre) => counts.set(genre, (counts.get(genre) ?? 0) + 1)),
	);
	const cloud = [...counts.entries()].sort(([left], [right]) => left.localeCompare(right));
	const largest = Math.max(1, ...cloud.map(([, count]) => count));
	return (
		<section className="genre-cloud page-section" aria-labelledby="genre-cloud-title">
			<div className="genre-cloud__heading">
				<span className="eyebrow">The room’s reading mood</span>
				<h2 id="genre-cloud-title">Genre cloud</h2>
				<p>Built from everyone’s preferred genres, with a little extra weight for books currently nominated.</p>
			</div>
			{cloud.length ? (
				<div className="genre-cloud__words">
					{cloud.map(([genre, count], index) => (
						<span
							key={genre}
							style={
								{
									'--genre-size': `${0.86 + (count / largest) * 1.35}rem`,
									'--genre-tilt': `${(index % 5) - 2}deg`,
								} as React.CSSProperties
							}
						>
							{genre}
						</span>
					))}
				</div>
			) : (
				<p className="genre-cloud__empty">The cloud will appear as people choose genres on their member tabs.</p>
			)}
		</section>
	);
}

function NominationsPage({
	data,
	actorId,
	mutate,
	openPicker,
	compactMode,
}: {
	data: BootstrapData;
	actorId: string;
	mutate: Mutate;
	openPicker: () => void;
	compactMode: boolean;
}) {
	const nominatedBookIds = new Set(data.cycle.nominations.map((item) => item.book.id));
	const shelfCandidates = data.lists.filter(
		(item, index, all) => !nominatedBookIds.has(item.book.id) && all.findIndex((other) => other.book.id === item.book.id) === index,
	);
	const canVote = data.cycle.allNominationsComplete && !data.cycle.allVotingComplete;
	const remaining = data.cycle.remainingNominationIds
		.map((id) => data.cycle.nominations.find((nomination) => nomination.id === id))
		.filter(Boolean) as Nomination[];
	return (
		<>
			<section className="page-intro page-section">
				<div>
					<span className="eyebrow">Our next read</span>
					<h1>Nominate & vote</h1>
					<p>
						Fill the ballot from anyone’s shelves, then upvote as many books as you genuinely fancy. Every tick and vote can be changed.
					</p>
				</div>
				<button className="button button--primary" onClick={openPicker}>
					<Icon name="plus" /> Nominate a book
				</button>
			</section>
			<section className="phase-grid page-section">
				<PhaseTracker data={data} phase="nominating" actorId={actorId} mutate={mutate} />
				<PhaseTracker data={data} phase="voting" actorId={actorId} mutate={mutate} />
			</section>
			{data.cycle.allVotingComplete && data.cycle.podium.length > 0 ? (
				<Podium data={data} actorId={actorId} mutate={mutate} />
			) : (
				<section className="page-section ballot-section">
					<div className="section-heading">
						<div>
							<span className="eyebrow">{canVote ? 'Your private ballot' : 'The longlist'}</span>
							<h2>
								{data.cycle.nominations.length
									? `${data.cycle.nominations.length} nominated ${data.cycle.nominations.length === 1 ? 'book' : 'books'}`
									: 'The ballot is empty'}
							</h2>
						</div>
						{canVote && (
							<span className="privacy-note">
								<Icon name="lock" size={14} /> Totals are hidden
							</span>
						)}
					</div>
					{!data.cycle.allNominationsComplete && data.cycle.nominations.length > 0 && (
						<div className="notice">
							<Icon name="sparkle" /> Voting unlocks when all six readers have finished nominating.
						</div>
					)}
					<div className="nomination-grid">
						{data.cycle.nominations.map((nomination) => (
							<NominationCard key={nomination.id} nomination={nomination} data={data} actorId={actorId} canVote={canVote} mutate={mutate} />
						))}
					</div>
					{!data.cycle.nominations.length && (
						<div className="empty-state">
							<span>✦</span>
							<h3>Start the ballot</h3>
							<p>Nominate something from the club shelves or search the catalogue.</p>
							<button className="button button--primary" onClick={openPicker}>
								Find a book
							</button>
						</div>
					)}
				</section>
			)}
			{data.cycle.allVotingComplete && remaining.length > 0 && (
				<section className="page-section remaining-section">
					<span className="eyebrow">Also nominated</span>
					<h2>The rest of our excellent options</h2>
					<p>Shown alphabetically, with no scores or ranking.</p>
					<div className="remaining-grid">
						{remaining.map((nomination) => (
							<article key={nomination.id}>
								<BookCover book={nomination.book} compact />
								<div>
									<h3>{nomination.book.title}</h3>
									<p>{nomination.book.author}</p>
								</div>
							</article>
						))}
					</div>
				</section>
			)}
			{shelfCandidates.length > 0 && !data.cycle.allVotingComplete && (
				<section className="page-section shelf-picks">
					<div className="section-heading">
						<div>
							<span className="eyebrow">From everyone’s lists</span>
							<h2>Ready to nominate</h2>
						</div>
						<span className="count-pill">{shelfCandidates.length}</span>
					</div>
					<div className="shelf-pick-row">
						{shelfCandidates.slice(0, 12).map((item) => {
							const owner = data.members.find((member) => member.id === item.memberId)!;
							return (
								<article key={item.id} className="shelf-pick">
									<BookCover book={item.book} compact />
									<div>
										<h3>{item.book.title}</h3>
										<p>{item.book.author}</p>
										<small>
											<Avatar member={owner} small /> {owner.name} · {LIST_LABELS[item.listType]}
										</small>
									</div>
									<button
										className="button button--small"
										onClick={() =>
											mutate(() => request('/api/nominations', { actorId, bookId: item.book.id }), `${item.book.title} nominated`)
										}
									>
										Nominate
									</button>
								</article>
							);
						})}
					</div>
				</section>
			)}
			{compactMode ? (
				<details className="compact-extras page-section">
					<summary>
						<span>
							<Icon name="sparkle" /> See the group’s genre cloud
						</span>
						<small>Reading mood</small>
					</summary>
					<GenreCloud data={data} />
				</details>
			) : (
				<GenreCloud data={data} />
			)}
		</>
	);
}

function ShelfBook({ item, owner, actorId, mutate }: { item: ListItem; owner: Member; actorId: string; mutate: Mutate }) {
	return (
		<article className="shelf-book">
			<BookCover book={item.book} compact />
			<div className="shelf-book__copy">
				<h3>{item.book.title}</h3>
				<p>{item.book.author}</p>
				{item.note ? <blockquote>“{item.note}”</blockquote> : <span className="no-note">No shelf note yet</span>}
				<div className="shelf-book__actions">
					<button
						onClick={() => mutate(() => request('/api/nominations', { actorId, bookId: item.book.id }), `${item.book.title} nominated`)}
					>
						<Icon name="sparkle" size={15} /> Nominate
					</button>
					<button
						onClick={() => {
							const note = window.prompt('Add a short note about this book', item.note);
							if (note !== null) void mutate(() => request(`/api/list-items/${item.id}`, { actorId, note }, 'PATCH'), 'Shelf note saved');
						}}
					>
						<Icon name="edit" size={15} /> Note
					</button>
					<button
						className="danger-link"
						onClick={() => {
							if (window.confirm(`Remove ${item.book.title} from ${owner.name}'s list?`))
								void mutate(() => request(`/api/list-items/${item.id}`, { actorId }, 'DELETE'), 'Book removed from shelf');
						}}
					>
						<Icon name="trash" size={15} /> Remove
					</button>
				</div>
			</div>
		</article>
	);
}

function GenreSelector({ data, owner, actorId, mutate }: { data: BootstrapData; owner: Member; actorId: string; mutate: Mutate }) {
	const [customGenre, setCustomGenre] = useState('');
	const selected = new Set(data.memberGenres[owner.id] ?? []);
	const suggestedLower = new Set<string>(GENRE_OPTIONS.map((genre) => genre.toLocaleLowerCase('en-GB')));
	const customGenres = [
		...new Map(
			Object.values(data.memberGenres)
				.flat()
				.filter((genre) => !suggestedLower.has(genre.toLocaleLowerCase('en-GB')))
				.map((genre) => [genre.toLocaleLowerCase('en-GB'), genre]),
		).values(),
	].sort((left, right) => left.localeCompare(right));
	const toggleGenre = (genre: string, isSelected: boolean) =>
		mutate(
			() => request('/api/member-genres/toggle', { actorId, memberId: owner.id, genre, selected: !isSelected }),
			`${genre} ${isSelected ? 'removed' : 'added'}`,
		);
	const genreButton = (genre: string, custom = false) => {
		const isSelected = selected.has(genre);
		return (
			<button
				key={genre}
				className={`${isSelected ? 'is-selected' : ''} ${custom ? 'is-custom' : ''}`}
				aria-pressed={isSelected}
				onClick={() => void toggleGenre(genre, isSelected)}
			>
				{isSelected && <Icon name="check" size={13} />} {genre}
			</button>
		);
	};
	return (
		<section className="panel genre-selector page-section">
			<div className="section-heading">
				<div>
					<span className="eyebrow">Reader’s card</span>
					<h2>Genres {owner.name} would like</h2>
					<p>
						Pick as many as feel right and tap again to remove one. The group’s tastes gather into the genre cloud on the nomination page.
					</p>
				</div>
			</div>
			<div className="genre-group">
				<span className="genre-group__label">Suggested</span>
				<div className="genre-chips" role="group" aria-label={`Suggested genres ${owner.name} would like to read`}>
					{GENRE_OPTIONS.map((genre) => genreButton(genre))}
				</div>
			</div>
			<div className="custom-genre-box">
				<div>
					<span className="genre-group__label">Write your own</span>
					<p>Anything goes: a broad genre, a niche subgenre or a mood. Add as many as you like.</p>
				</div>
				<form
					onSubmit={async (event) => {
						event.preventDefault();
						const genre = customGenre.trim().replace(/\s+/gu, ' ');
						if (!genre) return;
						const saved = await mutate(
							() => request('/api/member-genres/toggle', { actorId, memberId: owner.id, genre, selected: true }),
							`${genre} added`,
						);
						if (saved) setCustomGenre('');
					}}
				>
					<label htmlFor={`custom-genre-${owner.id}`}>Custom genre</label>
					<input
						id={`custom-genre-${owner.id}`}
						value={customGenre}
						onChange={(event) => setCustomGenre(event.target.value)}
						maxLength={40}
						placeholder="e.g. Cosy fantasy"
					/>
					<button className="button button--primary" disabled={!customGenre.trim()}>
						<Icon name="plus" size={15} /> Add genre
					</button>
				</form>
			</div>
			{customGenres.length > 0 && (
				<div className="genre-group genre-group--custom">
					<span className="genre-group__label">Created by the club · tap to reuse</span>
					<div className="genre-chips" role="group" aria-label={`Custom club genres ${owner.name} would like to read`}>
						{customGenres.map((genre) => genreButton(genre, true))}
					</div>
				</div>
			)}
		</section>
	);
}

function MemberPage({
	data,
	ownerId,
	actorId,
	mutate,
	openPicker,
	compactMode,
}: {
	data: BootstrapData;
	ownerId: string;
	actorId: string;
	mutate: Mutate;
	openPicker: (target: PickerTarget) => void;
	compactMode: boolean;
}) {
	const owner = data.members.find((member) => member.id === ownerId) ?? data.members[0];
	return (
		<>
			<section className="member-hero page-section" style={{ '--member-colour': owner.accent } as React.CSSProperties}>
				<Avatar member={owner} />
				<div>
					<span className="eyebrow">Personal shelves</span>
					<h1>{owner.name}’s books</h1>
					<p>Anyone can add, edit or nominate from here.</p>
				</div>
				<div className="member-stats">
					<span>
						<strong>{data.lists.filter((item) => item.memberId === owner.id).length}</strong> books saved
					</span>
				</div>
			</section>
			<div className="shelves-grid page-section">
				{(['want_to_read', 'recommend'] as ListType[]).map((listType) => {
					const items = data.lists.filter((item) => item.memberId === owner.id && item.listType === listType);
					return (
						<section className="panel shelf" key={listType}>
							<div className="section-heading">
								<div>
									<span className="eyebrow">{listType === 'recommend' ? 'Worth sharing' : 'For a future chapter'}</span>
									<h2>{LIST_LABELS[listType]}</h2>
								</div>
								<button className="button button--small" onClick={() => openPicker({ kind: 'shelf', memberId: owner.id, listType })}>
									<Icon name="plus" /> Add
								</button>
							</div>
							<div className="shelf-list">
								{items.map((item) => (
									<ShelfBook key={item.id} item={item} owner={owner} actorId={actorId} mutate={mutate} />
								))}
								{!items.length && (
									<div className="shelf-empty">
										<span>{listType === 'recommend' ? '✦' : '⌁'}</span>
										<p>
											{listType === 'recommend'
												? 'Share a book worth pressing into a friend’s hands.'
												: 'Save the books you keep meaning to read.'}
										</p>
										<button onClick={() => openPicker({ kind: 'shelf', memberId: owner.id, listType })}>Add the first book</button>
									</div>
								)}
							</div>
						</section>
					);
				})}
			</div>
			{compactMode ? (
				<details className="compact-extras page-section">
					<summary>
						<span>
							<Icon name="book" /> {owner.name}’s genre preferences
						</span>
						<small>Reading tastes</small>
					</summary>
					<GenreSelector data={data} owner={owner} actorId={actorId} mutate={mutate} />
				</details>
			) : (
				<GenreSelector data={data} owner={owner} actorId={actorId} mutate={mutate} />
			)}
		</>
	);
}

function HistoryReactionEditor({ entry, actorId, mutate }: { entry: ReadHistoryEntry; actorId: string; mutate: Mutate }) {
	const existing = entry.reactions.find((reaction) => reaction.memberId === actorId);
	const [status, setStatus] = useState<ReadingHistoryStatus | null>(existing?.status ?? null);
	const [rating, setRating] = useState<number | null>(existing?.rating ?? null);
	const [verdict, setVerdict] = useState(existing?.verdict ?? '');
	const [dirty, setDirty] = useState(false);
	const save = (next: { status?: ReadingHistoryStatus | null; rating?: number | null; verdict?: string } = {}) => {
		const payload = {
			status: next.status === undefined ? status : next.status,
			rating: next.rating === undefined ? rating : next.rating,
			verdict: next.verdict === undefined ? verdict : next.verdict,
		};
		void mutate(() => request(`/api/history/${entry.id}/reaction`, { actorId, ...payload }), 'Your reading memory was saved');
		setDirty(false);
	};
	return (
		<div className="history-reaction">
			<span className="history-reaction__label">Your memory · change or clear any choice</span>
			<div className="status-buttons" role="group" aria-label="Your reading status">
				{(
					[
						['finished', 'Finished'],
						['dnf', 'Didn’t finish'],
						['skipped', 'Skipped'],
					] as Array<[ReadingHistoryStatus, string]>
				).map(([value, label]) => (
					<button
						key={value}
						className={status === value ? 'is-selected' : ''}
						onClick={() => {
							const next = status === value ? null : value;
							setStatus(next);
							save({ status: next });
						}}
					>
						{status === value && <Icon name="check" size={12} />} {label}
					</button>
				))}
			</div>
			<div className="rating-row" aria-label="Your rating">
				<span>Rating</span>
				{[1, 2, 3, 4, 5].map((value) => (
					<button
						key={value}
						className={rating && value <= rating ? 'is-filled' : ''}
						aria-label={`${value} out of 5`}
						onClick={() => {
							const next = rating === value ? null : value;
							setRating(next);
							save({ rating: next });
						}}
					>
						<Icon name="star" size={16} />
					</button>
				))}
			</div>
			<div className="verdict-row">
				<input
					value={verdict}
					onChange={(event) => {
						setVerdict(event.target.value);
						setDirty(true);
					}}
					maxLength={280}
					placeholder="A one-line verdict…"
				/>
				{dirty && <button onClick={() => save()}>Save</button>}
			</div>
		</div>
	);
}

function HistoryEditorModal({
	entry,
	actorId,
	mutate,
	onClose,
}: {
	entry: ReadHistoryEntry;
	actorId: string;
	mutate: Mutate;
	onClose: () => void;
}) {
	const [meetingDate, setMeetingDate] = useState(
		entry.automaticallyArchived ? localDateTimeValue(entry.meetingDate) : (entry.meetingDate ?? '').slice(0, 10),
	);
	const [venue, setVenue] = useState(entry.venue ?? '');
	const [notes, setNotes] = useState(entry.notes);
	const [busy, setBusy] = useState(false);
	return (
		<Modal title={`Edit ${entry.book.title} in the archive`} onClose={onClose}>
			<form
				className="stack-form"
				onSubmit={async (event) => {
					event.preventDefault();
					setBusy(true);
					const saved = await mutate(
						() =>
							request(
								`/api/history/${entry.id}`,
								{
									actorId,
									meetingDate: meetingDate ? (entry.automaticallyArchived ? new Date(meetingDate).toISOString() : meetingDate) : null,
									venue,
									notes,
								},
								'PATCH',
							),
						'Archived meeting updated',
					);
					setBusy(false);
					if (saved) onClose();
				}}
			>
				<label>
					When did you discuss it?
					<input
						type={entry.automaticallyArchived ? 'datetime-local' : 'date'}
						value={meetingDate}
						onChange={(event) => setMeetingDate(event.target.value)}
					/>
				</label>
				<label>
					Venue
					<input value={venue} maxLength={160} onChange={(event) => setVenue(event.target.value)} placeholder="Optional venue" />
				</label>
				<label>
					Shared memory or meeting note
					<textarea rows={4} maxLength={600} value={notes} onChange={(event) => setNotes(event.target.value)} />
				</label>
				{entry.automaticallyArchived && (
					<p className="form-note">This updates the preserved meeting record as well as its place on the Read-history shelf.</p>
				)}
				<div className="modal__actions">
					<button type="button" className="button button--ghost" onClick={onClose} disabled={busy}>
						Cancel
					</button>
					<button className="button button--primary" disabled={busy}>
						{busy ? 'Saving…' : 'Save archive details'}
					</button>
				</div>
			</form>
		</Modal>
	);
}

function ArchivedMeetingRecords({ entry, members }: { entry: ReadHistoryEntry; members: Member[] }) {
	const memberById = new Map(members.map((member) => [member.id, member]));
	return (
		<div className="archive-records">
			{entry.questionJar && entry.questionJar.questions.length > 0 && (
				<details className="archive-record">
					<summary>
						<span>
							<Icon name="message" size={16} /> Discussion jar
						</span>
						<small>
							{entry.questionJar.questions.length} {entry.questionJar.questions.length === 1 ? 'question' : 'questions'}
						</small>
					</summary>
					{!entry.questionJar.revealed && (
						<p className="archive-record__note">This jar remained sealed; only your own question is readable.</p>
					)}
					<ol className="archived-questions">
						{entry.questionJar.questions.map((question) => (
							<li key={question.id} className={!question.question ? 'is-sealed' : ''}>
								<p>{question.question ?? 'A sealed question'}</p>
								<small>
									{memberById.get(question.memberId)?.name ?? 'A reader'} ·{' '}
									{question.spoilerScope === 'spoiler_free' ? 'spoiler-free' : 'whole-book'}
								</small>
							</li>
						))}
					</ol>
				</details>
			)}
			{entry.selection && (
				<details className="archive-record">
					<summary>
						<span>
							<Icon name="sparkle" size={16} /> How this book was chosen
						</span>
						<small>Preserved ballot</small>
					</summary>
					<div className="archived-podium">
						{[...entry.selection.podium]
							.sort((left, right) => left.rank - right.rank)
							.map((place) => (
								<div key={place.nominationId} className={place.nominationId === entry.selection?.selectedNominationId ? 'is-chosen' : ''}>
									<span>{place.rank}</span>
									<strong>{place.book.title}</strong>
									<small>
										{place.votes} {place.votes === 1 ? 'vote' : 'votes'}
										{place.nominationId === entry.selection?.selectedNominationId ? ' · chosen' : ''}
									</small>
								</div>
							))}
					</div>
					{entry.selection.remainingBooks.length > 0 && (
						<p className="archived-remaining">
							Also nominated, alphabetically and without scores: {entry.selection.remainingBooks.map((book) => book.title).join(', ')}.
						</p>
					)}
				</details>
			)}
		</div>
	);
}

function HistoryPage({
	data,
	actorId,
	mutate,
	openPicker,
}: {
	data: BootstrapData;
	actorId: string;
	mutate: Mutate;
	openPicker: () => void;
}) {
	const memberById = new Map(data.members.map((member) => [member.id, member]));
	const [editingEntry, setEditingEntry] = useState<ReadHistoryEntry | null>(null);
	return (
		<>
			<section className="page-intro history-intro page-section">
				<div>
					<span className="eyebrow">The club archive</span>
					<h1>Read-history shelf</h1>
					<p>Every finished club pick, with the where, when and what-we-thought kept between its covers.</p>
				</div>
				<button className="button button--primary" onClick={openPicker}>
					<Icon name="plus" /> Add a past read
				</button>
			</section>
			{data.history.length ? (
				<section className="history-shelf page-section">
					{data.history.map((entry) => {
						const scores = entry.reactions.map((reaction) => reaction.rating).filter((score): score is number => score !== null);
						const average = scores.length ? scores.reduce((total, score) => total + score, 0) / scores.length : null;
						const historyDate = parseHistoryDate(entry.meetingDate);
						return (
							<article key={entry.id} className="history-card">
								<div className="history-card__book">
									<BookCover book={entry.book} />
									<div className="history-card__spine" aria-hidden="true" />
								</div>
								<div className="history-card__copy">
									<div className="history-card__heading">
										<div>
											<span className="eyebrow">
												{historyDate
													? new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' }).format(historyDate)
													: 'Past read'}
											</span>
											<h2>{entry.book.title}</h2>
										</div>
										<button
											className="icon-button icon-button--quiet"
											onClick={() => setEditingEntry(entry)}
											aria-label={`Edit ${entry.book.title} archive details`}
										>
											<Icon name="edit" size={16} />
										</button>
									</div>
									<p className="book-author">by {entry.book.author}</p>
									{entry.venue && (
										<p className="history-venue">
											<Icon name="calendar" size={15} /> Discussed at {entry.venue}
										</p>
									)}
									{entry.notes && <blockquote>{entry.notes}</blockquote>}
									<ArchivedMeetingRecords entry={entry} members={data.members} />
									<div className="history-card__summary">
										<div className="mini-avatars">
											{entry.reactions.map((reaction) => {
												const member = memberById.get(reaction.memberId);
												return member ? <Avatar member={member} small key={member.id} /> : null;
											})}
										</div>
										{average && (
											<span>
												<Icon name="star" size={14} /> {average.toFixed(1)} club average
											</span>
										)}
									</div>
									{entry.reactions.some((reaction) => reaction.verdict) && (
										<div className="club-verdicts">
											{entry.reactions
												.filter((reaction) => reaction.verdict)
												.map((reaction) => (
													<p key={reaction.memberId}>
														“{reaction.verdict}” <span>— {memberById.get(reaction.memberId)?.name}</span>
													</p>
												))}
										</div>
									)}
									<HistoryReactionEditor entry={entry} actorId={actorId} mutate={mutate} />
								</div>
							</article>
						);
					})}
				</section>
			) : (
				<section className="history-empty page-section">
					<div className="empty-books" aria-hidden="true">
						<i />
						<i />
						<i />
						<i />
					</div>
					<span className="eyebrow">An empty archive is just a beginning</span>
					<h2>Shelve the books you’ve already shared</h2>
					<p>Add a past pick, then everyone can record whether they finished, give it stars and leave a one-line verdict.</p>
					<button className="button button--primary" onClick={openPicker}>
						Add the first past read
					</button>
				</section>
			)}
			{editingEntry && <HistoryEditorModal entry={editingEntry} actorId={actorId} mutate={mutate} onClose={() => setEditingEntry(null)} />}
		</>
	);
}

function BookPicker({ target, actorId, onDone, mutate }: { target: PickerTarget; actorId: string; onDone: () => void; mutate: Mutate }) {
	const [query, setQuery] = useState('');
	const [results, setResults] = useState<SearchBook[]>([]);
	const [searching, setSearching] = useState(false);
	const [searched, setSearched] = useState(false);
	const [searchError, setSearchError] = useState('');
	const [catalogueStatus, setCatalogueStatus] = useState<'live' | 'cached' | 'stale' | null>(null);
	const [manual, setManual] = useState(false);
	const [title, setTitle] = useState('');
	const [author, setAuthor] = useState('');
	const [historyBook, setHistoryBook] = useState<SearchBook | null>(null);
	const [meetingDate, setMeetingDate] = useState('');
	const [venue, setVenue] = useState('');
	const [historyNotes, setHistoryNotes] = useState('');
	const choose = async (book: SearchBook) => {
		if (target.kind === 'history') {
			setHistoryBook(book);
			return;
		}
		const work =
			target.kind === 'shelf'
				? () => request('/api/list-items', { actorId, memberId: target.memberId, listType: target.listType, book })
				: () => request('/api/nominations', { actorId, book });
		if (await mutate(work, target.kind === 'shelf' ? `${book.title} added to the shelf` : `${book.title} nominated`)) onDone();
	};
	const submitSearch = async (event: FormEvent) => {
		event.preventDefault();
		if (query.trim().length < 2) return;
		setSearching(true);
		setSearched(true);
		setSearchError('');
		setCatalogueStatus(null);
		try {
			const result = await searchBooks(query);
			setResults(result.books);
			setCatalogueStatus(result.catalogueStatus);
		} catch (error) {
			setResults([]);
			setSearchError(error instanceof Error ? error.message : 'The book catalogue is unavailable just now.');
		} finally {
			setSearching(false);
		}
	};
	return (
		<Modal
			title={
				target.kind === 'shelf'
					? `Add to ${LIST_LABELS[target.listType]}`
					: target.kind === 'history'
						? 'Add a past club read'
						: 'Nominate a book'
			}
			onClose={onDone}
			wide
		>
			{historyBook ? (
				<form
					className="history-add-form"
					onSubmit={async (event) => {
						event.preventDefault();
						const saved = await mutate(
							() => request('/api/history', { actorId, book: historyBook, meetingDate: meetingDate || null, venue, notes: historyNotes }),
							`${historyBook.title} added to read history`,
						);
						if (saved) onDone();
					}}
				>
					<div className="history-add-book">
						<BookCover book={historyBook} compact />
						<div>
							<span className="eyebrow">Selected book</span>
							<h3>{historyBook.title}</h3>
							<p>{historyBook.author}</p>
						</div>
						<button type="button" className="text-button" onClick={() => setHistoryBook(null)}>
							Change
						</button>
					</div>
					<div className="history-add-fields">
						<label>
							When did you discuss it?
							<input type="date" value={meetingDate} onChange={(event) => setMeetingDate(event.target.value)} />
						</label>
						<label>
							Where?
							<input value={venue} maxLength={160} onChange={(event) => setVenue(event.target.value)} placeholder="Optional venue" />
						</label>
					</div>
					<label>
						A shared memory
						<textarea
							rows={3}
							maxLength={600}
							value={historyNotes}
							onChange={(event) => setHistoryNotes(event.target.value)}
							placeholder="A memorable moment, theme or very strong snack…"
						/>
					</label>
					<div className="modal__actions">
						<button type="button" className="button button--ghost" onClick={onDone}>
							Cancel
						</button>
						<button className="button button--primary">
							<Icon name="archive" /> Shelve in history
						</button>
					</div>
				</form>
			) : (
				<>
					<form className="book-search" onSubmit={submitSearch}>
						<Icon name="search" />
						<input
							value={query}
							onChange={(event) => setQuery(event.target.value)}
							placeholder="Search by title, author or ISBN"
							autoFocus
						/>
						<button className="button button--primary" disabled={searching || query.trim().length < 2}>
							{searching ? 'Searching…' : 'Search'}
						</button>
					</form>
					<p className="catalogue-note">Book details and covers come from Open Library; saved searches keep working during an outage.</p>
					{searchError && (
						<div className="catalogue-status catalogue-status--error" role="alert">
							<div>
								<strong>Catalogue temporarily unavailable</strong>
								<p>{searchError}</p>
							</div>
							<button type="button" className="button button--ghost" onClick={() => setManual(true)}>
								Enter it manually
							</button>
						</div>
					)}
					{catalogueStatus === 'stale' && (
						<p className="catalogue-status catalogue-status--stale" role="status">
							Open Library is slow just now, so these are the most recent saved results.
						</p>
					)}
					<div className="search-results">
						{results.map((book) => (
							<button
								key={book.openLibraryKey ?? `${book.title}-${book.author}`}
								className="search-result"
								onClick={() => void choose(book)}
							>
								<BookCover book={book} compact />
								<span>
									<strong>{book.title}</strong>
									<small>
										{book.author}
										{book.firstPublishYear ? ` · ${book.firstPublishYear}` : ''}
									</small>
									{book.genres.length > 0 && <em>{book.genres.slice(0, 3).join(' · ')}</em>}
								</span>
								<span className="result-add">
									<Icon name="plus" />
								</span>
							</button>
						))}
						{searched && !searching && !searchError && !results.length && (
							<div className="empty-search">
								<p>No matching books found.</p>
							</div>
						)}
					</div>
					{!manual ? (
						<button className="text-button manual-toggle" onClick={() => setManual(true)}>
							Can’t find it? Add the details manually
						</button>
					) : (
						<form
							className="manual-book"
							onSubmit={(event) => {
								event.preventDefault();
								if (title.trim() && author.trim())
									void choose({ title, author, openLibraryKey: null, coverUrl: null, firstPublishYear: null, sourceUrl: null, genres: [] });
							}}
						>
							<span className="eyebrow">Manual entry</span>
							<div>
								<label>
									Title
									<input value={title} onChange={(event) => setTitle(event.target.value)} required maxLength={200} />
								</label>
								<label>
									Author
									<input value={author} onChange={(event) => setAuthor(event.target.value)} required maxLength={200} />
								</label>
								<button className="button button--primary">Add book</button>
							</div>
						</form>
					)}
				</>
			)}
		</Modal>
	);
}

function FeedbackModal({ actorId, onClose, mutate }: { actorId: string; onClose: () => void; mutate: Mutate }) {
	const [kind, setKind] = useState<'bug' | 'feature'>('feature');
	const [message, setMessage] = useState('');
	const [busy, setBusy] = useState(false);
	return (
		<Modal title="Send a note to the suggestion box" onClose={onClose}>
			<form
				className="stack-form feedback-form"
				onSubmit={async (event) => {
					event.preventDefault();
					if (!message.trim()) return;
					setBusy(true);
					const saved = await mutate(() => request('/api/feedback', { actorId, kind, message }), 'Thanks — your note is in the box');
					setBusy(false);
					if (saved) onClose();
				}}
			>
				<p>Found something odd, or thought of a small delight the site needs? Leave it here for Louis to find.</p>
				<div className="feedback-kind" role="group" aria-label="Note type">
					<button type="button" className={kind === 'bug' ? 'is-selected' : ''} onClick={() => setKind('bug')}>
						Bug report
					</button>
					<button type="button" className={kind === 'feature' ? 'is-selected' : ''} onClick={() => setKind('feature')}>
						Feature request
					</button>
				</div>
				<label>
					Your note
					<textarea
						rows={5}
						maxLength={1200}
						value={message}
						onChange={(event) => setMessage(event.target.value)}
						placeholder={
							kind === 'bug' ? 'What happened, and what were you trying to do?' : 'What would make the club site nicer or more useful?'
						}
						autoFocus
					/>
				</label>
				<div className="modal__actions">
					<button type="button" className="button button--ghost" onClick={onClose}>
						Cancel
					</button>
					<button className="button button--primary" disabled={busy || !message.trim()}>
						{busy ? 'Sending…' : 'Put it in the box'}
					</button>
				</div>
			</form>
		</Modal>
	);
}

function CompactMoreMenu({
	data,
	activeMember,
	onView,
	onChooseIdentity,
	onRefresh,
	onFeedback,
	onFullView,
	onLogout,
	onClose,
}: {
	data: BootstrapData;
	activeMember: Member;
	onView: (view: View) => void;
	onChooseIdentity: () => void;
	onRefresh: () => void;
	onFeedback: () => void;
	onFullView: () => void;
	onLogout: () => void;
	onClose: () => void;
}) {
	const act = (work: () => void) => {
		onClose();
		work();
	};
	return (
		<Modal title="More from the club" onClose={onClose}>
			<div className="compact-more">
				<button className="compact-more__identity" onClick={() => act(onChooseIdentity)}>
					<Avatar member={activeMember} />
					<span>
						<small>Browsing as</small>
						<strong>{activeMember.name}</strong>
						<em>Tap to change reader</em>
					</span>
					<Icon name="chevron" />
				</button>

				<div className="compact-more__section">
					<span className="compact-more__label">Browse a member’s shelves</span>
					<div className="compact-member-grid">
						{data.members.map((member) => (
							<button key={member.id} onClick={() => act(() => onView(`member/${member.id}`))}>
								<Avatar member={member} small />
								{member.name}
							</button>
						))}
					</div>
				</div>

				<div className="compact-more__links">
					<button onClick={() => act(() => onView('history'))}>
						<span>
							<Icon name="archive" /> Read-history shelf
						</span>
						<Icon name="chevron" size={15} />
					</button>
					<button onClick={() => act(onRefresh)}>
						<span>
							<Icon name="refresh" /> Refresh shared data
						</span>
						<Icon name="chevron" size={15} />
					</button>
					<button onClick={() => act(onFeedback)}>
						<span>
							<Icon name="message" /> Report a bug / request a feature
						</span>
						<Icon name="chevron" size={15} />
					</button>
					<button onClick={() => act(onFullView)}>
						<span>
							<Icon name="monitor" /> Switch to full view
						</span>
						<Icon name="chevron" size={15} />
					</button>
				</div>
				<button className="compact-more__leave" onClick={() => act(onLogout)}>
					Leave the private site
				</button>
			</div>
		</Modal>
	);
}

function AppShell({
	data,
	activeMemberId,
	view,
	onView,
	onChooseIdentity,
	onRefresh,
	onLogout,
	onFeedback,
	viewMode,
	onViewMode,
	children,
}: {
	data: BootstrapData;
	activeMemberId: string;
	view: View;
	onView: (view: View) => void;
	onChooseIdentity: () => void;
	onRefresh: () => void;
	onLogout: () => void;
	onFeedback: () => void;
	viewMode: ViewMode;
	onViewMode: (mode: ViewMode) => void;
	children: ReactNode;
}) {
	const [moreOpen, setMoreOpen] = useState(false);
	const activeMember = data.members.find((member) => member.id === activeMemberId) ?? data.members[0];
	const compactMode = viewMode === 'compact';
	return (
		<div className={`app-shell ${compactMode ? 'app-shell--compact' : ''}`}>
			<header className="site-header">
				<div className="header-main">
					<button className="brand" onClick={() => onView('meeting')}>
						<span>
							<Icon name="book" size={23} />
						</span>
						<div>
							<strong>Book Club</strong>
							<small>Six readers, one next chapter</small>
						</div>
					</button>
					<nav className="primary-nav" aria-label="Club pages">
						<button className={view === 'meeting' ? 'is-active' : ''} onClick={() => onView('meeting')}>
							<Icon name="calendar" /> Next meeting
						</button>
						<button className={view === 'nominations' ? 'is-active' : ''} onClick={() => onView('nominations')}>
							<Icon name="heart" /> Nominate & vote
						</button>
						<button className={view === 'history' ? 'is-active' : ''} onClick={() => onView('history')}>
							<Icon name="archive" /> Read history
						</button>
					</nav>
					<div className="header-actions">
						<button className="icon-button" onClick={onRefresh} aria-label="Refresh shared data">
							<Icon name="refresh" />
						</button>
						<button className="profile-button" onClick={onChooseIdentity}>
							<Avatar member={activeMember} small />
							<span className="profile-copy">
								<small>Browsing as</small>
								{activeMember.name}
							</span>
							<Icon name="chevron" size={14} />
						</button>
						<button className="logout-button" onClick={onLogout}>
							Leave
						</button>
					</div>
				</div>
				<div className="people-tabs-wrap">
					<div className="people-tabs-label">
						<Icon name="user" size={14} /> Member shelf tabs
					</div>
					<nav className="people-nav" aria-label="Member shelf tabs" role="tablist">
						{data.members.map((member) => (
							<button
								key={member.id}
								className={view === `member/${member.id}` ? 'is-active' : ''}
								onClick={() => onView(`member/${member.id}`)}
								role="tab"
								aria-selected={view === `member/${member.id}`}
							>
								<span className="tab-marker" style={{ background: member.accent }} />
								<span className="tab-copy">
									<strong>{member.name}</strong>
									<small>their shelves</small>
								</span>
							</button>
						))}
					</nav>
				</div>
			</header>
			<main>{children}</main>
			<footer className="site-footer">
				<div>
					<Icon name="book" />
					<span>Made for six friends and their ever-growing shelves.</span>
				</div>
				<div className="footer-actions">
					<button onClick={() => onViewMode(viewMode === 'compact' ? 'full' : 'compact')}>
						<Icon name={viewMode === 'compact' ? 'monitor' : 'phone'} size={15} />{' '}
						{viewMode === 'compact' ? 'Use full view' : 'Use compact view'}
					</button>
					<button onClick={onFeedback}>
						<Icon name="message" size={15} /> Report a bug / request a feature
					</button>
					<a href="https://openlibrary.org" target="_blank" rel="noreferrer">
						Book data by Open Library
					</a>
				</div>
			</footer>
			{compactMode && (
				<nav className="compact-bottom-nav" aria-label="Compact navigation">
					<button className={view === 'meeting' ? 'is-active' : ''} onClick={() => onView('meeting')}>
						<Icon name="calendar" />
						<span>Meeting</span>
					</button>
					<button className={view === 'nominations' ? 'is-active' : ''} onClick={() => onView('nominations')}>
						<Icon name="heart" />
						<span>Vote</span>
					</button>
					<button className={view.startsWith('member/') ? 'is-active' : ''} onClick={() => onView(`member/${activeMemberId}`)}>
						<Icon name="book" />
						<span>Shelves</span>
					</button>
					<button className={moreOpen || view === 'history' ? 'is-active' : ''} onClick={() => setMoreOpen(true)}>
						<Icon name="more" />
						<span>More</span>
					</button>
				</nav>
			)}
			{moreOpen && (
				<CompactMoreMenu
					data={data}
					activeMember={activeMember}
					onView={onView}
					onChooseIdentity={onChooseIdentity}
					onRefresh={onRefresh}
					onFeedback={onFeedback}
					onFullView={() => onViewMode('full')}
					onLogout={onLogout}
					onClose={() => setMoreOpen(false)}
				/>
			)}
		</div>
	);
}

export function App() {
	const [authState, setAuthState] = useState<'loading' | 'locked' | 'ready'>('loading');
	const [data, setData] = useState<BootstrapData | null>(null);
	const [activeMemberId, setActiveMemberId] = useState(() => localStorage.getItem('book-club-member') ?? 'dan');
	const [identityChosen, setIdentityChosen] = useState(() => Boolean(localStorage.getItem('book-club-member')));
	const [choosingIdentity, setChoosingIdentity] = useState(false);
	const [view, setView] = useState<View>(readView);
	const [picker, setPicker] = useState<PickerTarget | null>(null);
	const [feedbackOpen, setFeedbackOpen] = useState(false);
	const [viewMode, setViewMode] = useState<ViewMode>(readViewMode);
	const [compactPromptOpen, setCompactPromptOpen] = useState(false);
	const [toast, setToast] = useState('');
	const [error, setError] = useState('');

	const refresh = useCallback(
		async (memberId = activeMemberId, quiet = false) => {
			try {
				const next = await getBootstrap(memberId);
				setData(next);
				setAuthState('ready');
				if (!quiet) setError('');
			} catch (cause) {
				const message = cause instanceof Error ? cause.message : 'Could not refresh the club.';
				if (message.toLowerCase().includes('password')) {
					setAuthState('locked');
					setData(null);
				} else if (!quiet) setError(message);
			}
		},
		[activeMemberId],
	);

	useEffect(() => {
		void (async () => {
			try {
				if (await getSession()) await refresh(activeMemberId);
				else setAuthState('locked');
			} catch {
				setAuthState('locked');
			}
		})();
	}, []);
	useEffect(() => {
		const handler = () => setView(readView());
		window.addEventListener('hashchange', handler);
		return () => window.removeEventListener('hashchange', handler);
	}, []);
	useEffect(() => {
		if (authState !== 'ready') return;
		const interval = window.setInterval(() => {
			if (document.visibilityState === 'visible') void refresh(activeMemberId, true);
		}, 30_000);
		return () => window.clearInterval(interval);
	}, [authState, activeMemberId, refresh]);
	useEffect(() => {
		if (!toast) return;
		const timeout = window.setTimeout(() => setToast(''), 2600);
		return () => window.clearTimeout(timeout);
	}, [toast]);
	useEffect(() => {
		if (authState === 'ready' && identityChosen && !hasViewModePreference() && isLikelyMobileDevice()) {
			setCompactPromptOpen(true);
		}
	}, [authState, identityChosen]);

	const navigate = (next: View) => {
		window.location.hash = next;
		setView(next);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};
	const chooseIdentity = (memberId: string) => {
		localStorage.setItem('book-club-member', memberId);
		setActiveMemberId(memberId);
		setIdentityChosen(true);
		setChoosingIdentity(false);
		void refresh(memberId);
	};
	const chooseViewMode = (mode: ViewMode) => {
		localStorage.setItem('book-club-view-mode', mode);
		setViewMode(mode);
		setCompactPromptOpen(false);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};
	const mutate = async (work: () => Promise<unknown>, message?: string) => {
		setError('');
		try {
			await work();
			await refresh(activeMemberId, true);
			if (message) setToast(message);
			return true;
		} catch (cause) {
			setError(cause instanceof Error ? cause.message : 'Please try that again.');
			return false;
		}
	};
	const handleLogout = async () => {
		await logout();
		setAuthState('locked');
		setData(null);
	};

	if (authState === 'loading')
		return (
			<div className="loading-screen">
				<div className="loading-book">
					<span />
					<span />
				</div>
				<p>Opening the shelves…</p>
			</div>
		);
	if (authState === 'locked') return <LoginScreen onSuccess={() => refresh(activeMemberId)} />;
	if (!data)
		return (
			<div className="loading-screen">
				<p>We couldn’t open the shelves.</p>
				<button className="button button--primary" onClick={() => void refresh()}>
					Try again
				</button>
			</div>
		);
	if (!identityChosen) return <IdentityChooser members={data.members} onChoose={chooseIdentity} />;

	const ownerId = view.startsWith('member/') ? view.slice('member/'.length) : '';
	return (
		<AppShell
			data={data}
			activeMemberId={activeMemberId}
			view={view}
			onView={navigate}
			onChooseIdentity={() => setChoosingIdentity(true)}
			onRefresh={() => void refresh()}
			onLogout={() => void handleLogout()}
			onFeedback={() => setFeedbackOpen(true)}
			viewMode={viewMode}
			onViewMode={chooseViewMode}
		>
			{error && (
				<div className="global-error" role="alert">
					<span>{error}</span>
					<button onClick={() => setError('')} aria-label="Dismiss">
						<Icon name="close" size={16} />
					</button>
				</div>
			)}
			{view === 'meeting' && <MeetingPage data={data} actorId={activeMemberId} mutate={mutate} compactMode={viewMode === 'compact'} />}
			{view === 'nominations' && (
				<NominationsPage
					data={data}
					actorId={activeMemberId}
					mutate={mutate}
					openPicker={() => setPicker({ kind: 'nomination' })}
					compactMode={viewMode === 'compact'}
				/>
			)}
			{view === 'history' && (
				<HistoryPage data={data} actorId={activeMemberId} mutate={mutate} openPicker={() => setPicker({ kind: 'history' })} />
			)}
			{ownerId && (
				<MemberPage
					data={data}
					ownerId={ownerId}
					actorId={activeMemberId}
					mutate={mutate}
					openPicker={setPicker}
					compactMode={viewMode === 'compact'}
				/>
			)}
			{picker && <BookPicker target={picker} actorId={activeMemberId} onDone={() => setPicker(null)} mutate={mutate} />}
			{choosingIdentity && <IdentityChooser members={data.members} onChoose={chooseIdentity} onClose={() => setChoosingIdentity(false)} />}
			{feedbackOpen && <FeedbackModal actorId={activeMemberId} onClose={() => setFeedbackOpen(false)} mutate={mutate} />}
			{compactPromptOpen && <CompactModePrompt onChoose={chooseViewMode} />}
			{toast && (
				<div className="toast" role="status">
					<Icon name="check" /> {toast}
				</div>
			)}
		</AppShell>
	);
}
